import { Monitor, Moon, Sun } from 'lucide-react';
import { Button } from 'v5/design-system/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { WithServices } from 'v5/platform/ts/services';
import { WithThemeService } from 'v5/services/front/ThemeService/ThemeService';

export interface ThemeSwitcherProps extends WithServices<[WithThemeService]> {
  variant?: 'dropdown' | 'button' | 'icon';
  className?: string;
}

/**
 * Theme switcher component
 *
 * @example
 * ```tsx
 * // In sidebar footer:
 * <ThemeSwitcher variant="dropdown" />
 *
 * // As icon button:
 * <ThemeSwitcher variant="icon" />
 * ```
 */
export function ThemeSwitcher({
  services,
  variant = 'dropdown',
  className
}: ThemeSwitcherProps) {
  const { themeService } = services;
  const preference = themeService.useThemePreference();
  const theme = themeService.useEffectiveTheme();

  if (variant === 'icon') {
    const tooltipText = 'Change theme';

    return (
      <DropdownMenu>
        <SimpleTooltip title={tooltipText} align="start" delayDuration={1500}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              layout="icon"
              className={className}
              aria-label={`Current theme: ${theme} (${preference})`}
            >
              {preference === 'system' ? (
                <Monitor className="size-4" />
              ) : theme === 'dark' ? (
                <Moon className="size-4" />
              ) : (
                <Sun className="size-4" />
              )}
            </Button>
          </DropdownMenuTrigger>
        </SimpleTooltip>
        <DropdownMenuContent align="start" side="top" className="w-56">
          <DropdownMenuLabel>Choose theme</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuRadioGroup
            value={preference}
            onValueChange={value =>
              themeService.setThemePreference(value as any)
            }
          >
            <DropdownMenuRadioItem value="light">
              <Sun className="size-4 mr-2" />
              <div className="flex flex-col">
                <span>Light</span>
                <span className="text-xs text-fg-muted">
                  Clean, bright interface
                </span>
              </div>
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dark">
              <Moon className="size-4 mr-2" />
              <div className="flex flex-col">
                <span>Dark</span>
                <span className="text-xs text-fg-muted">Easy on the eyes</span>
              </div>
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="system">
              <Monitor className="size-4 mr-2" />
              <div className="flex flex-col">
                <span>System</span>
                <span className="text-xs text-fg-muted">
                  Match system preference
                </span>
              </div>
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (variant === 'button') {
    return (
      <Button
        variant="ghost"
        size="sm"
        className={className}
        onClick={() => {
          // Toggle between light and dark
          themeService.setThemePreference(theme === 'dark' ? 'light' : 'dark');
        }}
      >
        {theme === 'dark' ? (
          <Moon className="size-4" />
        ) : (
          <Sun className="size-4" />
        )}
        <span className="flex-1 text-left">
          {theme === 'dark' ? 'Dark' : 'Light'} Mode
        </span>
      </Button>
    );
  }

  // Default: dropdown variant
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-3 px-3 py-2 rounded text-[13px] cursor-pointer transition-colors w-full border border-transparent text-fg-default hocus:border-border-muted hocus:bg-bg-subtle/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
          {theme === 'dark' ? (
            <Moon className="size-4 flex-shrink-0" />
          ) : (
            <Sun className="size-4 flex-shrink-0" />
          )}
          <span className="flex-1 text-left truncate">Theme</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="right" className="w-56">
        <DropdownMenuLabel>Choose theme</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={preference}
          onValueChange={value => themeService.setThemePreference(value as any)}
        >
          <DropdownMenuRadioItem value="light">
            <Sun className="size-4 mr-2" />
            <div className="flex flex-col">
              <span>Light</span>
              <span className="text-xs text-fg-muted">
                Clean, bright interface
              </span>
            </div>
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon className="size-4 mr-2" />
            <div className="flex flex-col">
              <span>Dark</span>
              <span className="text-xs text-fg-muted">Easy on the eyes</span>
            </div>
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <Monitor className="size-4 mr-2" />
            <div className="flex flex-col">
              <span>System</span>
              <span className="text-xs text-fg-muted">
                Match system preference
              </span>
            </div>
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
